import { useEffect, useState } from 'react';
import * as Icons from '../icons';
import { SUGGESTIONS, USER } from '../data/mocks';
import { compactNumber } from '../util';
import { FiltersModal } from '../components/FiltersModal';
import { CriteriaModal } from '../components/CriteriaModal';
import type { Criterion } from '../types';
import { runAgent, createSearch, runResults, checkHealth, updateSearch } from '../api';
import type { SearchDoc } from '../api';

interface Props {
  onRun: (state: { search: SearchDoc; filters: Record<string, unknown>; criteria: Criterion[]; matchCount: number }) => void;
}

type Mode = 'similar' | 'jd' | 'boolean' | 'manual';

const modeBtnCls = (active: boolean) =>
  'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors ' +
  (active
    ? 'border-[var(--accent)] bg-[var(--accent-muted)] text-[var(--accent-strong)]'
    : 'border-[var(--border-default)] bg-[var(--surface)] text-[var(--text-primary)] hover:border-[var(--accent)]');

export function NewSearchView({ onRun }: Props) {
  const [mode, setMode] = useState<Mode>('jd');
  const [prompt, setPrompt] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [serverMode, setServerMode] = useState<'live' | 'demo' | 'down'>('demo');
  const [draftSearch, setDraftSearch] = useState<SearchDoc | null>(null);

  useEffect(() => {
    checkHealth()
      .then(h => setServerMode(h.mode))
      .catch(() => setServerMode('down'));
  }, []);

  async function runAgentFor(text: string) {
    setSubmitted(true);
    setAgentLoading(true);
    setFilterValues({});
    setCriteria([]);
    setMatchCount(null);
    setDraftSearch(null);
    try {
      const r = await runAgent(text);
      const parsedFilters = r.interpretation?.filters || {};
      const parsedCriteria = r.interpretation?.criteria || [];
      setFilterValues(parsedFilters);
      setCriteria(parsedCriteria);
      // Persist a draft search so we can probe + run results against it.
      const search = await createSearch({
        prompt: text,
        filters: parsedFilters,
        criteria: parsedCriteria,
      });
      setDraftSearch(search);
      // Cheap match-count probe (no Grok eval needed for the count).
      try {
        const probe = await runResults(search._id, { page: 1, pageSize: 1, evaluate: false });
        setMatchCount(probe.total);
      } catch (e) {
        console.warn('[probe]', e);
      }
    } catch (e) {
      console.warn('[agent]', e);
    } finally {
      setAgentLoading(false);
    }
  }

  async function commitAndRun() {
    if (!draftSearch) return;
    // Persist any modal edits to filters/criteria before navigating.
    const updated = await updateSearch(draftSearch._id, {
      filters: filterValues,
      criteria,
    });
    onRun({ search: updated, filters: filterValues, criteria, matchCount: matchCount ?? 0 });
  }

  return (
    <div className="flex flex-1 items-start justify-center px-8 pb-10 pt-20">
      <div className="flex w-full max-w-[720px] flex-col gap-6">
        <h1 className="m-0 mb-2 text-center text-2xl font-medium text-[var(--text-primary)]">
          Hey {USER.firstName}, who are you looking for?
        </h1>
        <div className="text-center text-[12px] text-[var(--text-muted)]">
          {serverMode === 'live'
            ? '● Connected to live Coresignal'
            : serverMode === 'down'
            ? '⚠ Server unreachable'
            : '○ Demo mode (no API key set)'}
        </div>

        <div className="flex flex-wrap justify-center gap-2.5">
          <button className={modeBtnCls(mode === 'similar')} onClick={() => setMode('similar')}>
            <Icons.People /> Find Similar
          </button>
          <button className={modeBtnCls(mode === 'jd')} onClick={() => setMode('jd')}>
            <Icons.Description /> Job Description
          </button>
          <button className={modeBtnCls(mode === 'boolean')} onClick={() => setMode('boolean')}>
            <Icons.Sigma /> Boolean
          </button>
          <button className={modeBtnCls(mode === 'manual')} onClick={() => setMode('manual')}>
            <Icons.Cursor /> Select Manually
          </button>
        </div>

        <div className="relative rounded-xl border-2 border-[var(--accent)] bg-[var(--surface)] p-5 pr-16">
          <textarea
            className="w-full min-h-[64px] resize-none border-0 bg-transparent text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)]"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Software Engineers with 5+ yrs of experience at fintech companies in the Bay Area"
            rows={3}
          />
          <button
            className="absolute bottom-4 right-4 flex h-8 w-8 items-center justify-center rounded-full border border-[var(--accent)] bg-[var(--surface)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => prompt.trim() && runAgentFor(prompt.trim())}
            disabled={agentLoading}
          >
            <Icons.ArrowRight />
          </button>
        </div>

        {!submitted ? (
          <div className="flex flex-col overflow-hidden rounded-md border border-[var(--border-default)] bg-[var(--surface)]">
            {SUGGESTIONS.map((s, i) => (
              <div
                key={i}
                className="cursor-pointer border-b border-[var(--border-muted)] px-5 py-3 text-sm last:border-b-0 hover:bg-[var(--surface-muted)]"
                onClick={() => {
                  setPrompt(s);
                  runAgentFor(s);
                }}
              >
                {s}
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 flex w-full max-w-[720px] flex-col gap-4">
            <PreviewCard avatar={<Icons.People />}>
              <p className="m-0 text-[var(--text-primary)]">{prompt}</p>
            </PreviewCard>

            <PreviewCard
              avatar={<Icons.AutoAwesome style={{ width: 16, height: 16 }} />}
              avatarDark
            >
              <p className="m-0 mb-2 text-[var(--text-primary)]">
                I've set these{' '}
                <a className="cursor-pointer text-[var(--text-link)] hover:underline">
                  <Icons.Funnel style={{ width: 12, height: 12, marginRight: 2, display: 'inline' }} /> filters
                </a>{' '}
                based on what you're looking for
                {agentLoading ? (
                  <span className="text-[12px] text-[var(--text-muted)]"> · parsing with Grok…</span>
                ) : matchCount != null ? (
                  <span className="text-[var(--text-muted)]"> ({compactNumber(matchCount)} matches)</span>
                ) : null}
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {agentLoading ? (
                  <span className="text-[12px] text-[var(--text-muted)]">Reading the prompt…</span>
                ) : (
                  <FilterChips values={filterValues} />
                )}
                <a
                  className="cursor-pointer text-sm text-[var(--text-link)] hover:underline"
                  onClick={() => setFiltersOpen(true)}
                >
                  Edit filters
                </a>
              </div>
            </PreviewCard>

            <PreviewCard avatar={<Icons.Diamond />}>
              <p className="m-0 mb-2 text-[var(--text-primary)]">
                Add these{' '}
                <a className="cursor-pointer text-[var(--text-link)] hover:underline">
                  <Icons.Diamond style={{ width: 12, height: 12, marginRight: 2, display: 'inline' }} /> criteria
                </a>{' '}
                to rank your matches
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {agentLoading ? (
                  <span className="text-[12px] text-[var(--text-muted)]">Extracting soft criteria…</span>
                ) : criteria.length ? (
                  criteria.map(c => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 rounded-sm bg-[var(--accent-subtle)] px-2 py-0.5 text-[13px] text-[var(--accent-strong)]"
                    >
                      {c.text.replace(/^The candidate( has experience in| has| is)?\s*/i, '').replace(/\.$/, '')}
                    </span>
                  ))
                ) : (
                  <span className="text-[12px] text-[var(--text-muted)]">
                    No soft criteria detected — add some to rank matches.
                  </span>
                )}
                <a
                  className="cursor-pointer text-sm text-[var(--text-link)] hover:underline"
                  onClick={() => setCriteriaOpen(true)}
                >
                  Edit criteria
                </a>
              </div>
            </PreviewCard>

            <div className="mt-1 flex items-center justify-end gap-3">
              <a
                className="cursor-pointer text-sm text-[var(--text-muted)] hover:underline"
                onClick={() => {
                  setPrompt('');
                  setSubmitted(false);
                  setFilterValues({});
                  setCriteria([]);
                  setDraftSearch(null);
                  setMatchCount(null);
                }}
              >
                Reset Search
              </a>
              <button
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--accent-strong)] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={agentLoading || !draftSearch}
                onClick={commitAndRun}
              >
                Run Search
              </button>
            </div>
          </div>
        )}
      </div>

      <FiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        matchCount={matchCount ?? 0}
        initialValues={filterValues}
        onSave={setFilterValues}
      />
      <CriteriaModal
        open={criteriaOpen}
        onClose={() => setCriteriaOpen(false)}
        initial={criteria}
        onSave={setCriteria}
      />
    </div>
  );
}

function PreviewCard({
  avatar,
  avatarDark,
  children,
}: {
  avatar: React.ReactNode;
  avatarDark?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3.5 rounded-xl border border-[var(--border-default)] bg-[var(--surface)] p-4">
      <div
        className={
          'flex h-8 w-8 flex-none items-center justify-center rounded-full ' +
          (avatarDark ? 'bg-[#1f1f23] text-white' : 'bg-[var(--surface-tertiary)] text-[var(--text-muted)]')
        }
      >
        {avatar}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function FilterChips({ values }: { values: Record<string, unknown> }) {
  const tokens = summarize(values);
  if (!tokens.length) return <span className="text-[12px] text-[var(--text-muted)]">No filters detected.</span>;
  const head = tokens.slice(0, 4);
  const extra = tokens.length - head.length;
  return (
    <>
      {head.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {t.connector ? <span className="text-[var(--text-muted)]">{t.connector}</span> : null}
          <span className="inline-flex items-center gap-1 rounded-sm bg-[var(--accent-subtle)] px-2 py-0.5 text-[13px] text-[var(--accent-strong)]">
            {t.label}
            {t.count && t.count > 1 ? <span className="text-[12px] text-[var(--text-muted)]"> +{t.count - 1}</span> : null}
          </span>
        </span>
      ))}
      {extra > 0 ? (
        <span className="inline-flex items-center rounded-sm bg-[var(--accent-subtle)] px-2 py-0.5 text-[13px] text-[var(--accent-strong)]">
          +{extra} more filters
        </span>
      ) : null}
    </>
  );
}

interface ChipToken {
  label: string;
  count?: number;
  connector?: string;
}
function summarize(v: Record<string, unknown>): ChipToken[] {
  const out: ChipToken[] = [];
  const arr = (k: string) => (Array.isArray(v[k]) ? (v[k] as unknown[]) : []);
  const num = (k: string) => (typeof v[k] === 'number' ? (v[k] as number) : null);
  const titles = arr('jobTitles');
  if (titles.length) out.push({ label: String(titles[0]), count: titles.length });
  const past = arr('pastJobTitles');
  if (past.length) out.push({ label: 'past: ' + String(past[0]), count: past.length });
  const locs = arr('locations');
  if (locs.length) out.push({ label: locs.map(String).slice(0, 2).join(', '), connector: titles.length ? 'in' : undefined });
  const minExp = num('yearsOfExp');
  const maxExp = num('maxExp');
  if (minExp != null || maxExp != null) {
    const part = minExp != null && maxExp != null
      ? `${minExp}–${maxExp} yrs`
      : minExp != null
      ? `${minExp}+ yrs`
      : `≤${maxExp} yrs`;
    out.push({ label: part });
  }
  const skills = arr('skills');
  if (skills.length) out.push({ label: String(skills[0]), count: skills.length });
  const cos = arr('selectedCompanies');
  if (cos.length) out.push({ label: '@ ' + String(cos[0]), count: cos.length });
  const ind = arr('industries');
  if (ind.length) out.push({ label: String(ind[0]), count: ind.length });
  const sizes = arr('companySizes');
  if (sizes.length) out.push({ label: String(sizes[0]) + ' employees', count: sizes.length });
  const stages = arr('fundingStages');
  if (stages.length) out.push({ label: String(stages[0]).replace(/_/g, ' '), count: stages.length });
  const langs = arr('languages');
  if (langs.length) out.push({ label: String(langs[0]) + '-speaking', count: langs.length });
  const unis = arr('selectedUnis');
  if (unis.length) out.push({ label: String(unis[0]), count: unis.length });
  const levels = arr('jobTitleLevels');
  if (levels.length) out.push({ label: String(levels[0]).replace(/_/g, ' '), count: levels.length });
  const roles = arr('jobTitleRoles');
  if (roles.length) out.push({ label: String(roles[0]), count: roles.length });
  const pf = arr('pfFilterType');
  if (pf.length) out.push({ label: String(pf[0]).replace(/_/g, ' '), count: pf.length });
  return out;
}
